import { useState, useEffect } from 'react';
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  Tooltip, 
  Legend 
} from 'recharts';
import { TimeRange, getCategoryStats, CategoryStats } from '@/services/analyticsService';
import { supabase } from '@/lib/supabase';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, ChartPieIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface ReplicategoriesPanelProps {
  timeRange?: TimeRange;
  standalone?: boolean;
}

interface CategoryData {
  name: string;
  value: number;
}

const ReplicategoriesPanel = ({ 
  timeRange: externalTimeRange,
  standalone = false 
}: ReplicategoriesPanelProps) => {
  const isMobile = useIsMobile();
  const [data, setData] = useState<CategoryData[]>([]);
  const [loading, setLoading] = useState(true);
  // Updated color palette for the pie chart sections - New vibrant color scheme
  const COLORS = ['#6366F1', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6', '#14B8A6', '#F43F5E', '#3B82F6', '#06B6D4', '#84CC16'];

  useEffect(() => {
    const fetchAllCategories = async () => {
      setLoading(true);      
      try {
        const { data, error } = await supabase.from('products').select('*');
        
        if (error) {
          console.error('Error fetching categories:', error);
          toast.error('Failed to load category statistics');
          return;
        }
        
        const categoryMap = new Map<string, number>();
        
        data?.forEach(item => {
          const categories = [
            item['category[0]'],
            item['category[1]'],
            item['category[2]']
          ];
          
          categories.forEach(category => {
            if (category && typeof category === 'string' && category.trim() !== '') {
              if (categoryMap.has(category)) {
                categoryMap.set(category, categoryMap.get(category)! + 1);
              } else {
                categoryMap.set(category, 1);
              }
            }
          });
        });
        
        const categoryData = Array.from(categoryMap.entries())
          .map(([name, count]) => ({ name, value: count }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 10); // Return top 10 categories
          
        setData(categoryData);
      } catch (error) {
        console.error('Error fetching category data:', error);
        toast.error('Failed to load category statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchAllCategories();
  }, []);

  const renderChart = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin text-primary" />
        </div>
      );
    }

    if (data.length === 0) {
      return (
        <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
          No category data available
        </div>
      );
    }
    
    return (
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={80}
            paddingAngle={2}
            dataKey="value"
            label={({ name, percent }) => 
              isMobile 
                ? `${(percent * 100).toFixed(0)}%`
                : `${name} (${(percent * 100).toFixed(0)}%)`
            }
            labelLine={!isMobile}
          >
            {data.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={COLORS[index % COLORS.length]} 
              />
            ))}
          </Pie>
          <Tooltip 
            formatter={(value, name) => [`${value} items`, name]}
            contentStyle={{ 
              backgroundColor: 'var(--background)', 
              borderColor: 'var(--border)',
              borderRadius: '0.375rem',
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              padding: isMobile ? '8px' : '10px'
            }}
          />
          <Legend
            layout={isMobile ? "horizontal" : "vertical"}
            verticalAlign={isMobile ? "bottom" : "middle"}
            align={isMobile ? "center" : "right"}
            wrapperStyle={
              isMobile 
                ? { fontSize: '0.75rem' } 
                : { fontSize: '0.875rem', right: 0, maxHeight: 150, overflowY: 'auto' }
            }
          />
        </PieChart>
      </ResponsiveContainer>
    );
  };
  // If used outside a card, render with a full card wrapper
  if (standalone) {
    return (
      <Card className="bg-background border border-border/50 shadow-subtle h-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ChartPieIcon className="h-5 w-5 text-[#6366F1]" />
            Replibrary Categories
          </CardTitle>
          <CardDescription>
            Product category distribution
          </CardDescription>
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0">
          <div className="h-[200px] sm:h-[300px] w-full">
            {renderChart()}
          </div>
        </CardContent>
      </Card>
    );
  }

  return renderChart();
};

export default ReplicategoriesPanel;
